import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { UserProvider } from './UserContext';
import './App.css';

import Login         from './Login';
import PasswordSetup from './PasswordSetup';
import ResetPassword from './ResetPassword';
import Navbar        from './Navbar';
import PIPs          from './Pips/Pips';
import UnSanctions   from './UnSanctions/UnSanctions';
import UkSanctions   from './UkSanctions/UkSanctions';
import AuditTrail    from './AuditTrail';
import DataCapturer  from './DataCapturer/DataCapturer';
import ManageCustomers from './Customers/ManageCustomers';
import ManageCases     from './Cases/ManageCases';
import ManageAlerts       from './TM/ManageAlerts';
import ManageTransactions from './TM/ManageTransactions';
import ManageRules        from './TM/ManageRules';
import ManageLimitRules   from './Limits/ManageLimitRules';
import ManageSTRReports   from './STR/ManageSTRReports';
import ManageReviews      from './Reviews/ManageReviews';
import Dashboard          from './Dashboard/Dashboard';
import ManageWatchlist    from './Watchlist/ManageWatchlist';

import ManageUsers        from './Administrator/ManageUsers';
import ManageRoles        from './Administrator/ManageRoles';
import ManageOrganisations from './Administrator/ManageOrganisations.js';
import ManagePackages     from './Administrator/ManagePackages';
import ManageInvoices     from './Invoices/ManageInvoices';
import OrgMessages        from './Messages/OrgMessages';
import PipSearchHistory   from './Administrator/PipSearchHistory';
import ManagePermissions  from './Administrator/ManagePermissions';

import PrivateRoute    from './PrivateRoute';
import DisclaimerModal from './components/DisclaimerModal';
import AppHeader       from './components/AppHeader';
import { ConfirmProvider } from './components/ConfirmProvider';
import MtcGate         from './components/MtcGate';
import FeedbackButton  from './components/FeedbackButton';
import ManageFeedback  from './Feedback/ManageFeedback';

// Layout wraps all authenticated pages with Navbar + footer
function Layout({ children }) {
  const location = useLocation();
  const isLoginPage = ['/', '/login', '/set-password', '/reset-password'].includes(location.pathname);

  // Login pages: no app chrome, but visitors can still send feedback.
  if (isLoginPage) return <>{children}<FeedbackButton publicMode /></>;

  const currentYear = new Date().getFullYear();

  return (
    <div className="app-layout">
      <Navbar />
      <div className="content-wrapper">
        <AppHeader />
        {children}
      </div>
      <FeedbackButton />
      <footer className="app-footer">
        <div className="app-footer__text">
          &copy; {currentYear} All Rights Reserved | PIP Intel Namibia |{' '}
          <a
            href={process.env.REACT_APP_SITE_URL || 'https://pipintel.com'}
            target="_blank"
            rel="noopener noreferrer"
            className="app-footer__link"
          >
            {(process.env.REACT_APP_SITE_URL || 'https://pipintel.com').replace(/^https?:\/\//, '')}
          </a>
        </div>
        <div className="app-footer__logos">
          <span className="app-footer__flag">
            <img
              src={require('./assets/Flag_of_Namibia.svg.png')}
              alt="Namibia Flag"
              className="app-footer__flag-img"
            />
          </span>
          <span className="app-footer__flag">
            <img
              src={require('./assets/1.jpg')}
              alt="Logo"
              className="app-footer__flag-img"
            />
          </span>
        </div>
      </footer>
    </div>
  );
}

function AppContent({ showDisclaimer, handleAgree }) {
  return (
    <Layout>
      {showDisclaimer && <DisclaimerModal onAgree={handleAgree} />}
      <Routes>
        <Route path="/"            element={<Login />} />
        <Route path="/login"       element={<Login />} />
        <Route path="/set-password" element={<PasswordSetup />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Protected Routes */}
        <Route
          path="/Pips/pips"
          element={<PrivateRoute menu="pips"><PIPs /></PrivateRoute>}
        />
        <Route
          path="/un-sanctions"
          element={<PrivateRoute menu="un_sanctions"><UnSanctions /></PrivateRoute>}
        />
        <Route
          path="/uk-sanctions"
          element={<PrivateRoute menu="uk_sanctions"><UkSanctions /></PrivateRoute>}
        />
        <Route
          path="/customers"
          element={<PrivateRoute menu="kyc"><ManageCustomers /></PrivateRoute>}
        />
        {/* MTC25-26-O AML operational pages — gated by REACT_APP_SHOW_MTC_MENUS.
            When the flag is unset/false, MtcGate renders a "feature disabled"
            page instead of the underlying component so that direct URL
            navigation behaves the same as the hidden sidebar entry. */}
        <Route
          path="/cases"
          element={<PrivateRoute menu="kyc" require="cases.read"><ManageCases /></PrivateRoute>}
        />
        <Route
          path="/transactions"
          element={<MtcGate><PrivateRoute menu="transactions" require="transactions.read"><ManageTransactions /></PrivateRoute></MtcGate>}
        />
        <Route
          path="/tm-alerts"
          element={<MtcGate><PrivateRoute menu="tm_alerts" require="tm_alerts.read"><ManageAlerts /></PrivateRoute></MtcGate>}
        />
        <Route
          path="/tm-rules"
          element={<MtcGate><PrivateRoute menu="tm_rules" require="tm_rules.read"><ManageRules /></PrivateRoute></MtcGate>}
        />
        <Route
          path="/limit-rules"
          element={<MtcGate><PrivateRoute menu="limits" require="limits.read"><ManageLimitRules /></PrivateRoute></MtcGate>}
        />
        <Route
          path="/str-reports"
          element={<MtcGate><PrivateRoute menu="str_reports" require="str_reports.read"><ManageSTRReports /></PrivateRoute></MtcGate>}
        />
        <Route
          path="/customer-reviews"
          element={<MtcGate><PrivateRoute menu="cdd_reviews" require="customer_reviews.read"><ManageReviews /></PrivateRoute></MtcGate>}
        />
        <Route
          path="/dashboard"
          element={<PrivateRoute menu="dashboard"><Dashboard /></PrivateRoute>}
        />
        <Route
          path="/watchlist"
          element={<MtcGate><PrivateRoute menu="watchlist" require="org_watchlist.read"><ManageWatchlist /></PrivateRoute></MtcGate>}
        />
        <Route
          path="/audit"
          element={<PrivateRoute><AuditTrail /></PrivateRoute>}
        />
        {/* Legacy URL → redirect to the new dedicated route */}
        <Route
          path="/DataCapturer/datacapturer"
          element={<Navigate to="/datacapturer/domestic-pip" replace />}
        />
        {/* Data Capturer sub-routes — one form per URL */}
        <Route
          path="/datacapturer"
          element={<Navigate to="/datacapturer/domestic-pip" replace />}
        />
        {/* Data Capturer sub-routes — server enforces fine-grained permission.
            Frontend hides the Navbar entries the user can't use; landing here
            without permission shows a "no access" message inside the form. */}
        <Route
          path="/datacapturer/domestic-pip"
          element={<PrivateRoute><DataCapturer mode="domestic" /></PrivateRoute>}
        />
        <Route
          path="/datacapturer/foreign-pip"
          element={<PrivateRoute><DataCapturer mode="foreign" /></PrivateRoute>}
        />
        <Route
          path="/datacapturer/import-pips"
          element={<PrivateRoute><DataCapturer mode="import" /></PrivateRoute>}
        />
        <Route
          path="/datacapturer/upload-un-sanctions"
          element={<PrivateRoute><DataCapturer mode="upload-un" /></PrivateRoute>}
        />
        <Route
          path="/datacapturer/upload-uk-sanctions"
          element={<PrivateRoute><DataCapturer mode="upload-uk" /></PrivateRoute>}
        />
        <Route
          path="/datacapturer/capture-customer"
          element={<Navigate to="/customers" replace />}
        />
        {/* All access is permission-based — server enforces fine-grained perms;
            frontend uses PrivateRoute require="..." to hide pages the user can't open. */}
        <Route
          path="/administrator/manageusers"
          element={<PrivateRoute require="users.read"><ManageUsers /></PrivateRoute>}
        />
        <Route
          path="/administrator/manageroles"
          element={<PrivateRoute require="roles.read" staffOnly><ManageRoles /></PrivateRoute>}
        />
        <Route
          path="/administrator/ManageOrganisations"
          element={<PrivateRoute require="organisations.read"><ManageOrganisations /></PrivateRoute>}
        />
        <Route
          path="/administrator/managepackages"
          element={<PrivateRoute require="packages.read"><ManagePackages /></PrivateRoute>}
        />
        {/* Invoices: PIP Intel staff — checked in the page and the API */}
        <Route
          path="/administrator/invoices"
          element={<PrivateRoute requireAny={['invoices.read', 'invoices.view_own']}><ManageInvoices /></PrivateRoute>}
        />
        {/* Messages to organisation managers: PIP Intel staff only */}
        <Route
          path="/administrator/messages"
          element={<PrivateRoute require="org_messages.read" staffOnly><OrgMessages /></PrivateRoute>}
        />
        <Route
          path="/administrator/manage-permissions"
          element={<PrivateRoute require="permissions.read"><ManagePermissions /></PrivateRoute>}
        />
        <Route
          path="/pip-search-history"
          element={<PrivateRoute menu="pip_search_history"><PipSearchHistory /></PrivateRoute>}
        />
        {/* Feedback: PIP Intel staff (no organisation) — checked in the page and the API */}
        <Route
          path="/feedback"
          element={<PrivateRoute><ManageFeedback /></PrivateRoute>}
        />
      </Routes>
    </Layout>
  );
}

function App() {
  // Show disclaimer if user is logged in but hasn't agreed in this session
  const [showDisclaimer, setShowDisclaimer] = useState(() => {
    const token = localStorage.getItem('token');
    const agreed = localStorage.getItem('pip-terms-agreed');
    return !!token && agreed !== 'true';
  });

  useEffect(() => {
    const handleLogin = () => {
      // Always require fresh agreement on each new login
      localStorage.removeItem('pip-terms-agreed');
      setShowDisclaimer(true);
    };
    window.addEventListener('pip-login-success', handleLogin);
    return () => window.removeEventListener('pip-login-success', handleLogin);
  }, []);

  const handleAgree = () => setShowDisclaimer(false);

  return (
    <UserProvider>
      <ConfirmProvider>
        <Router>
          <AppContent showDisclaimer={showDisclaimer} handleAgree={handleAgree} />
        </Router>
      </ConfirmProvider>
    </UserProvider>
  );
}

export default App;
